/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 org-wide user profile (/users/:userId/profile): who someone is and how
 * to reach them. Read-only; anyone signed in may open it. People who manage
 * users get the same "Manage access" button the classic page offers.
 *
 * The data is the classic page's: ReadOnlyUserProfile fetches it and passes
 * it in.
 */

import PropTypes from "prop-types";
import moment from "moment";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { AlertCircle, UserCog } from "lucide-react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDButton from "components/MDButton";
import PageHeader from "components/v2/PageHeader";
import KeyFieldStrip from "components/v2/KeyFieldStrip";
import UserLink from "components/v2/UserLink";
import { v2 as t, v2Tones, V2_DISPLAY_FONT_FAMILY, V2_HEADER_GRADIENT } from "styles/tokens/v2";

const cardSx = { bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: "10px", p: { xs: 2, md: 2.5 } };

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");

export function fullName(p) {
  return [p?.first_name, p?.middle_name, p?.last_name].map(cap).filter(Boolean).join(" ") || p?.username || "No name set";
}

/** The real role assignment, else the legacy role string. */
export function roleNames(p) {
  const names = [p?.base_role_name, ...(p?.addon_role_names || [])].filter(Boolean);
  if (names.length) return names;
  return p?.role ? [String(p.role).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())] : [];
}

function initials(p) {
  const letters = [p?.first_name, p?.last_name].filter(Boolean).map((n) => n.charAt(0).toUpperCase()).join("");
  return letters || (p?.username || "?").charAt(0).toUpperCase();
}

function Avatar({ profile }) {
  const size = 72;
  if (profile?.avatar) {
    return (
      <Box
        component="img"
        src={profile.avatar}
        alt=""
        sx={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `1px solid ${t.border}` }}
      />
    );
  }
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        background: V2_HEADER_GRADIENT,
        color: t.onPrimary,
        fontFamily: V2_DISPLAY_FONT_FAMILY,
        fontSize: 26,
        fontWeight: 600,
      }}
    >
      {initials(profile)}
    </Box>
  );
}
Avatar.propTypes = { profile: PropTypes.object.isRequired };

function RoleChip({ children }) {
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", height: 24, px: 1.1, borderRadius: "12px", fontSize: 12.5, fontWeight: 600, bgcolor: v2Tones.info.bg, color: v2Tones.info.fg }}
    >
      {children}
    </Box>
  );
}
RoleChip.propTypes = { children: PropTypes.node.isRequired };

function Shell({ children }) {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 6 }}>{children}</Box>
    </DashboardLayout>
  );
}
Shell.propTypes = { children: PropTypes.node.isRequired };

export default function UserProfileV2({ profile, loading, error, canManageAccess, onManageAccess }) {
  if (loading) {
    return (
      <Shell>
        <Box aria-busy="true" aria-label="Loading profile">
          <Skeleton variant="text" width={240} height={36} />
          <Skeleton variant="text" width={180} sx={{ mb: 2.5 }} />
          <Box sx={cardSx}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Skeleton variant="circular" width={72} height={72} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="25%" />
              </Box>
            </Box>
            <Skeleton variant="rounded" height={56} sx={{ mt: 2.5 }} />
          </Box>
        </Box>
      </Shell>
    );
  }

  if (error || !profile) {
    return (
      <Shell>
        <PageHeader title="User profile" />
        <Box role="alert" sx={{ ...cardSx, display: "flex", alignItems: "center", gap: 1.5, color: t.text2, fontSize: 14 }}>
          <AlertCircle size={18} color={v2Tones.danger.fg} aria-hidden="true" />
          {error || "This profile could not be loaded. Try again."}
        </Box>
      </Shell>
    );
  }

  const name = fullName(profile);
  const roles = roleNames(profile);
  const subtitle = [profile.designation, profile.department].filter(Boolean).join(" · ");
  const manager = profile.manager_name ? <UserLink userId={profile.manager_id} name={profile.manager_name} tone="link" /> : null;
  const formatDate = (d, f) => (d ? moment(d).format(f) : null);

  return (
    <Shell>
      <PageHeader
        title={name}
        summary={subtitle || null}
        actions={canManageAccess ? (
          <MDButton variant="outlined" color="dark" startIcon={<UserCog size={16} />} onClick={onManageAccess}>
            Manage access
          </MDButton>
        ) : null}
      />

      <Box component="section" aria-label="Profile" sx={cardSx}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Avatar profile={profile} />
          <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
            <Typography component="h2" sx={{ fontFamily: V2_DISPLAY_FONT_FAMILY, fontSize: 18, fontWeight: 600, color: t.text, overflowWrap: "anywhere" }}>
              {name}
            </Typography>
            <Typography sx={{ fontSize: 14, color: t.text2 }}>{profile.designation || "No designation set"}</Typography>
            {roles.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                {roles.map((r) => <RoleChip key={r}>{r}</RoleChip>)}
              </Box>
            )}
          </Box>
        </Box>

        <KeyFieldStrip
          items={[
            { label: "Department", value: profile.department || null },
            { label: "Email", value: profile.email || null },
            { label: "Phone", value: profile.phone || null },
            { label: roles.length > 1 ? "Roles" : "Role", value: roles.join(", ") || null },
            { label: "Manager", value: manager },
            { label: "Joined", value: formatDate(profile.joining_date, "MMM D, YYYY") },
          ]}
        />
      </Box>

      <Box component="section" aria-label="Account" sx={{ ...cardSx, mt: 2.5 }}>
        <Typography component="h2" sx={{ fontFamily: V2_DISPLAY_FONT_FAMILY, fontSize: 16, fontWeight: 600, color: t.text, mb: 2 }}>
          Account
        </Typography>
        <Box>
          <KeyFieldStrip
            items={[
              { label: "Username", value: profile.username || null },
              { label: "Last sign-in", value: formatDate(profile.last_login, "MMM D, YYYY, h:mm A") },
            ]}
          />
        </Box>
      </Box>
    </Shell>
  );
}

UserProfileV2.propTypes = {
  profile: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  canManageAccess: PropTypes.bool.isRequired,
  onManageAccess: PropTypes.func.isRequired,
};

UserProfileV2.defaultProps = { profile: null, error: null };
